import { FormEvent, FunctionComponent, useCallback, useMemo, useState } from 'react';
import { StepContainer } from '../../../StepContainer';
import styles from './styles.module.scss';
import { useBookingRequestCheckoutContext } from '../../../CheckoutContext/BookingRequestContext';
import { DateInputs } from '../../RequestFlow/DatesStep/DateInputs';
import { OffersSelect } from '../../../OffersSelect';
import { AddOnsSelect } from '../../../AddonsSelect';
import { DateBookingRequestCheckoutState, GroupBookingRequestCheckoutState } from '@shared/types';
import { isOfferDateValid } from '../../../shared/isOfferDateValid';

export type DatesStepProps = {};

export const DatesStep: FunctionComponent<DatesStepProps> = () => {
    const { course, educator, checkoutState, submitResponse } = useBookingRequestCheckoutContext();

    const initialSelectedOffers = useMemo((): { [offerId: string]: number } => {
        const firstOffer = course.offers[0];
        if (firstOffer && isOfferDateValid(firstOffer)) {
            return {
                [firstOffer._id]: 1,
            };
        }
        return {};
    }, [course.offers]);

    const [selectedDates, setSelectedDates] = useState<Array<string>>(['']);
    const [offers, setOffers] = useState<{ [offerId: string]: number }>(initialSelectedOffers);
    const [addons, setAddons] = useState<{ [addonId: string]: number }>({});
    const hidePricing = useMemo(
        () => checkoutState.origin !== 'marketplace' && educator.feature_flags?.hide_pricing,
        [checkoutState.origin, educator.feature_flags]
    );

    const selectedOffers = useMemo(() => {
        return Object.entries(offers).map(([offerId, quantity]) => {
            return { offerId, quantity };
        });
    }, [offers]);

    const selectedAddons = useMemo(() => {
        return Object.entries(addons).map(([addonId, quantity]) => {
            return { addonId, quantity };
        });
    }, [addons]);

    const spacesRequested = useMemo(() => {
        return Object.values(offers).reduce((spaces, quantity) => {
            return spaces + quantity;
        }, 0);
    }, [offers]);

    const onContinue = useCallback(() => {
        submitResponse({
            actionType: 'dates and prices selected',
            payload: {
                selectedDates,
                selectedOffers,
                selectedAddons,
            },
        });
    }, [submitResponse, selectedDates, selectedAddons, selectedOffers]);

    const onBackClick = useCallback(() => {
        return submitResponse({ actionType: 'go back', payload: {} });
    }, [submitResponse]);

    const optimisticCheckoutState = useMemo(():
        | DateBookingRequestCheckoutState
        | GroupBookingRequestCheckoutState => {
        return {
            ...checkoutState,
            stepState: {
                ...checkoutState.stepState,
                dates: {
                    status: 'complete',
                    state: {
                        preferredDates: selectedDates,
                        addOns: selectedAddons,
                        offers: selectedOffers,
                        spacesRequested,
                    },
                },
            },
        };
    }, [checkoutState, selectedDates, selectedAddons, selectedOffers, spacesRequested]);

    const onFormSubmit = useCallback((event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
    }, []);

    return (
        <form onSubmit={onFormSubmit}>
            <StepContainer
                course={course}
                optimisticCheckoutState={optimisticCheckoutState}
                onContinueClick={onContinue}
                onBackClick={onBackClick}
            >
                <div className={styles.container}>
                    <DateInputs dates={selectedDates} setDates={setSelectedDates} />
                    <h2>Offers</h2>
                    <OffersSelect
                        offers={course.offers}
                        availableSpaces={Infinity}
                        selectedOffers={offers}
                        hidePricing={hidePricing}
                        setSelectedOffers={setOffers}
                        currency={course.currency}
                    />
                    {course.add_ons?.length ? (
                        <>
                            <h2>Add-ons</h2>
                            <AddOnsSelect
                                addOns={course.add_ons || []}
                                selectedAddOns={addons}
                                setSelectedAddOns={setAddons}
                                currency={course.currency}
                            />
                        </>
                    ) : null}
                </div>
            </StepContainer>
        </form>
    );
};
